import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import { OrgAcademicCalendarRow, OrgReportsApi } from '@/services/org-reports.api';
import { ACADEMIC_CALENDAR_TERMS, ACADEMIC_CALENDAR_TYPES } from '@/services/academic.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Academic Calendar — org tier, read-only. Cross-school events, matching
 * the Departments/Lessons report pattern. Session Year is a per-school
 * lookup (academic_years lives in each school's own schema), so it's
 * disabled until a specific school is picked — same cascade shape as the
 * Lessons report's Class/Section/Subject picker. Management (create/edit/
 * delete, generate bulk working days) stays at school tier.
 */
export function OrgAcademicCalendarPage() {
  const [schoolId, setSchoolId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [term, setTerm] = useState('');
  const [month, setMonth] = useState<number | ''>('');
  const [type, setType] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-academic-calendar-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: years = [] } = useQuery({
    queryKey: ['org-academic-calendar-years', schoolId],
    queryFn: () => OrgPromotionApi.academicYearsForSchool(schoolId),
    enabled: !!schoolId,
  });

  const params = {
    schoolId: schoolId || undefined,
    academicYearId: (schoolId && academicYearId) || undefined,
    term: term || undefined,
    month: month || undefined,
    type: type || undefined,
  };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-academic-calendar', params],
    queryFn: () => OrgReportsApi.academicCalendar(params),
    placeholderData: keepPreviousData,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportAcademicCalendar(format, params);
    } finally {
      setBusy(null);
    }
  };

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setAcademicYearId('');
  }

  return (
    <>
      <PageHeader
        title="Academic Calendar"
        description="Exams, teacher trainings, and other calendar events, across your organization's schools. Read-only — management happens at each school."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-44"
          value={academicYearId}
          onChange={(e) => setAcademicYearId(e.target.value)}
          disabled={!schoolId}
          title={schoolId ? undefined : 'Select a school to filter by session year'}
        >
          <option value="">All Years</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select className="!w-32" value={term} onChange={(e) => setTerm(e.target.value)}>
          <option value="">All Terms</option>
          {ACADEMIC_CALENDAR_TERMS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select className="!w-36" value={month} onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All Months</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
        <Select className="!w-44" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All Types</option>
          {ACADEMIC_CALENDAR_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">School</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Event</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Term</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Session Year</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFetching ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                  No calendar events for the selected filters.
                </td>
              </tr>
            ) : (
              rows.map((r: OrgAcademicCalendarRow) => (
                <tr key={`${r.schoolId}:${r.id}`}>
                  <td className="px-4 py-2.5 text-sm font-medium text-slate-800">{r.schoolName}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-700">{r.name}</td>
                  <td className="px-4 py-2.5 text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.color || undefined }} />
                      {r.type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{r.term ?? '—'}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{r.date}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{r.academicYearName ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
